<template>
<w-sliding-popup ref="popup" title="시뮬레이션 예측" width="1000px" @close="onClose" :default-content="false">
  <div slot="content" class="body-content-no-scroll" style="height:100%;" v-loading="loading" :element-loading-text="loadingText">
    <el-scrollbar>
      <!-- 조건 설정 -->
      <!-- 190628 아래줄(line 7)에 advanced-input 클라스 추가되었음 -->
      <div class="condition-set-box mr-b25 advanced-input">
        <ul>
          <li><span class="stress">대상장비</span>{{objLabel}}</li>
          <li>
            <span class="stress">시뮬레이션 기준</span>
            <span>
              <el-radio-group v-model="simulationType">
                <el-radio :label="simulationTypeDefine.PERIOD">기간</el-radio>
                <el-radio :label="simulationTypeDefine.CAPACITY">용량</el-radio>
              </el-radio-group>
            </span>
          </li>
          <li v-show="simulationType === simulationTypeDefine.PERIOD">
            <span class="stress">예측기간</span>
            <el-input-number ref="predictPeriodField" class="mr-r4" style="width:95px;" size="mini" v-model="predictPeriod" controls-position="right" :min="1" :max="maxPredictPeriod" :precision="0" @change="onPredictPeriodChange"/>
            <el-select class="smart-analyzer mr-r4" style="width:75px;" size="small" v-model="predictPeriodUnit" @change="onPredictPeriodUnitChange">
              <el-option v-for="item in predictPeriodUnits" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </li>
          <li>
            <el-row v-if="obj && obj.objManagetypeId === 'DBMS'" class="stress" style="width: 125px">
              <span class="mr-r2">시뮬레이션 자원</span>
              <el-tooltip effect="dark" placement="bottom" content="호스트 서버가 등록되어있는 경우에만 시뮬레이션 예측을 수행할 수 있습니다.">
                <i class="fas fa-info-circle c-pointer"></i>
              </el-tooltip>
            </el-row>
            <span v-else class="stress">시뮬레이션 자원</span>
            <el-table-wrapper class="mr-t10" v-loading="rscLoading" :max-height="165" :columns="rscColumns" :data="baseCapacity ? rscTableDataForCapacity : rscTableDataForPeriod" :column-default="columnDefault" :pagination="false" @selection-change="onRscSelectionChange">
              <template slot-scope="scope" slot="capacity-slot">
                <div class="vertical-center-box">
                  <el-input-number class="mr-r4" style="width:65px;top:0;" v-model="scope.row.scaleUpCapacity" :min="1" :max="9999" :precision="0" :controls="false" @change="onScaleUpCapacityChange"/>
                  <el-select style="width:60px;" v-model="scope.row.capacityUnit">
                    <el-option v-for="item in capacityUnits" :key="item.value" :label="item.value" :value="item.value"/>
                  </el-select>
                </div>
              </template>
            </el-table-wrapper>
          </li>
          <el-row class="mr-t10" style="margin-bottom:5px;" type="flex" justify="end">
            <el-button type="primary" class="smart-primary" :disabled="disabledSimulation" @click="executeSimulation">시뮬레이션 예측</el-button>
          </el-row>
        </ul>
      </div>
      <!-- 시뮬레이션 예측 결과 -->
      <div v-if="!isEmpty(predictData)" class="simulation-result-wrapper save-for-pdf">
        <div class="title-popup">시뮬레이션 예측결과
          <el-button class="smart-btn save-pdf-btn" style="float:right; margin-right: 2px" data-html2canvas-ignore="true" @click="exportPdf"> PDF 저장</el-button>
        </div>
        <div class="info-box mr-b10">
          <ul>
            <li><span class="stress">대상장비</span>{{objLabel}}</li>
            <li><span class="stress">예측기간</span>{{predictPeriodLabel}} ({{predictPeriodDetailLabel}})</li>
          </ul>
        </div>
        <!-- 요약 -->
        <el-table-wrapper class="mr-b25" :columns="simulationColumns" height="100%" :data="predictSummaryDatas" :column-default="columnDefault" :pagination="false">
          <template slot-scope="scope" slot="recommend-slot">
            <span v-if="scope.row.isRecommend">
              <i class="el-icon-caret-top" style="color:#e43757; margin-right:2px;"></i>
            </span>
            {{scope.row.recommendContent}}
          </template>
        </el-table-wrapper>
        <!-- 상세 -->
        <el-collapse class="square-icon" v-model="rscCollapseList">
          <el-collapse-item v-for="(data, index) in predictDetailDatas" :name="data.hashkeyIdString" :key="data.hashkeyIdString">
            <template slot="title">
              {{data.title}}
              <span v-if="data.isRecommendScaleUp" class="warning" style="margin-left: 14px;">
                {{data.recommendScaleUpLabel}} 권고
              </span>
            </template>
            <div>
              <el-row class="result-box" type="flex" justify="start">
                <div class="current-status">
                  <div class="title">사용률</div>
                  <div class="chart-box">
                    <highcharts :class="`export-pdf-${index}-circular`" v-show="!isExportingPdf" :options="data.currentPieChartOptions" />
                    <img :class="`export-pdf-${index}-circular`" v-show="isExportingPdf" />
                  </div>
                  <div class="text-box" :style="{ marginTop: data.isCpu ? '6px' : null, fontWeight: 500 }">현재 사용률: {{data.userateLabel}}</div>
                  <div v-if="!data.isCpu" class="text-box">
                    <span class="detail">
                      {{data.totalSizeLabel}} 중 <span class="text-em">{{data.usedSizeLabel}}</span> 사용
                    </span>
                  </div>
                </div>
                <div class="predict-result">
                  <div class="title">시뮬레이션 예측
                    <span class="max-value-info">예측 기간 동안 최대값 <span class="mr-l4 text-em">{{data.maxValueLabel}}</span></span>
                  </div>
                  <div class="chart-bpx">
                    <highcharts :class="`export-pdf-${index}-chart`" v-show="!isExportingPdf" :options="data.predictChartOptions" />
                    <img :class="`export-pdf-${index}-chart`" v-show="isExportingPdf" />
                  </div>
                  <div class="threshold-box">
                    <ul>
                      <li><span :style="`color:${thresholdRowColor}`">80%</span> : {{data.thresholdRowLabel}}</li>
                      <li><span :style="`color:${thresholdMediumColor}`">90%</span> : {{data.thresholdMediumLabel}}</li>
                      <li><span :style="`color:${thresholdHighColor}`">100%</span> : {{data.thresholdHighLabel}}</li>
                    </ul>
                  </div>
                </div>
              </el-row>
              <!-- 하단 차트 -->
              <div v-if="data.isRecommendScaleUp">
                <hr class="mr-t15 mr-b10" />
                <div class="warning">{{data.recommendScaleUpLabel}} 시 예상 추이</div>
                <div class="mr-b10"></div>
                <highcharts :class="`export-pdf-${index}-trend`" v-show="!isExportingPdf" :options="data.simulationChartOptions" />
                <img :class="`export-pdf-${index}-trend`" v-show="isExportingPdf" />
                <!-- <div class="popup-f-result chart-container">
                </div> -->
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </el-scrollbar>
  </div>
</w-sliding-popup>
</template>

<script>
import { isNil, isEmpty, clone, cloneDeep, forEach, findIndex, sortBy, map } from 'lodash';
import moment from 'moment';
import JsPdf from 'jspdf';
import canvg from 'canvg-browser';
import Highcharts from 'highcharts';
import Html2Canvas from 'html2canvas';
import { columnDefault } from '@/constants/table';
import { lineOptions, chartColorSet } from '@/constants/chart-options';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { removeTime } from '@/utils/date';
import MsgUtil from '@/utils/message';
import { convertValuByUnit, getUnitData, convertValueByUnitData } from '@/utils/number';
import predictPerfUtil from '@/utils/predict-performance';
import SmartAnalysisApi from '@/api/common/smart-analysis';

const simulationTypeDefine = {
  PERIOD: 'period',
  CAPACITY: 'capacity',
};

const pieChartOptions = {
  chart: {
    type: 'pie',
    height: '160px',
    margin: [0, 0, 0, 0],
    spacingTop: 0,
    spacingBottom: 0,
    spacingLeft: 0,
    spacingRight: 0,
  },
  title: {
    verticalAlign: 'middle',
    floating: true,
    y: 6,
  },
  credits: false,
  plotOptions: {
    pie: {
      dataLabels: {
        enabled: false,
      },
    },
  },
  tooltip: {
    headerFormat: null,
    pointFormat: '<b>{point.name}</b>: {point.percentage:.2f} %',
  },
  series: [],
};

const lineChartOptions = cloneDeep(lineOptions);
lineChartOptions.chart.height = 100;
lineChartOptions.colors = [chartColorSet[0], '#Fb4e7c'];
lineChartOptions.legend = {
  align: 'center',
  veritcalAlign: 'bottom',
};

export default {
  name: 'WSimulationPredictPopup',
  data() {
    return {
      loading: false,
      loadingText: null,
      popupWidth: 1000,
      columnDefault,
      /* 장비 정보 */
      obj: null,
      objLabel: null,
      objRegistDatetime: null,
      /* 자원 정보 */
      rscLoading: false,
      rscBaseColumns: [
        {
          type: 'selection',
          align: 'center',
          width: 50,
        },
        {
          prop: 'rscName',
          label: '자원',
        },
        {
          prop: 'rscKeyName',
          label: '자원키',
        },
      ],
      rscTableDataForPeriod: [], // 기간 기준 자원 테이블 데이터
      rscTableDataForCapacity: [], // 용량 기준 자원 테이블 데이터
      rscSelection: [], // 선택한 자원 배열
      capacityUnits: [
        {
          value: 'MB',
        },
        {
          value: 'GB',
        },
        {
          value: 'TB',
        },
      ],
      /* 시뮬레이션 예측 조건 */
      simulationPeriodColumns: [
        {
          prop: 'rscName',
          label: '자원',
        },
        {
          prop: 'currentPerf',
          label: '현재 성능',
        },
        {
          prop: 'recommendContent',
          label: '용량 증설 권고',
          scopedSlot: 'recommend-slot',
        },
      ],
      simulationCapacityColumns: [
        {
          prop: 'rscName',
          label: '자원',
        },
        {
          prop: 'currentPerf',
          label: '현재 성능',
        },
        {
          prop: 'condition',
          label: '시뮬레이션 조건',
        },
        {
          prop: 'result',
          label: '시뮬레이션 결과',
        },
      ],
      simulationColumns: [],
      simulationTypeDefine,
      simulationType: simulationTypeDefine.PERIOD,
      dataStoragePeriodBasicinfo: null, // 데이터 보관 주기 객체
      predictPeriod: 3,
      predictPeriodUnits: [
        {
          label: '일',
          value: predictPerfUtil.predictPeriodUnitDefine.DAY,
        },
        {
          label: '주',
          value: predictPerfUtil.predictPeriodUnitDefine.WEEK,
        },
        {
          label: '개월',
          value: predictPerfUtil.predictPeriodUnitDefine.MONTH,
        },
      ],
      predictPeriodUnit: predictPerfUtil.predictPeriodUnitDefine.DAY,
      maxPredictPeriod: null,
      predictTaskName: 'simulationTask',
      /* 시뮬레이션 예측 결과 */
      simulationPredictPeriod: null,
      simulationPredictPeriodUnit: null,
      predictData: null, // 시뮬레이션 예측 결과 데이터
      predictPeriodLabel: null, // 예측 기간 정보 (N일 / N주 / N개월)
      predictPeriodDetailLabel: null, // 예측 기간 상세 정보 (YY.MM.DD hh:mm ~ YY.MM.DD hh:mm)
      predictSummaryDatas: [], // 시뮬레이션 예측 결과 요약 정보
      rscCollapseList: [],
      predictDetailDatas: [], // 시뮬레이션 예측 결과 자원 별 상세 정보 (배열)
      thresholdRowColor: '#e7b40d',
      thresholdMediumColor: '#fb8637',
      thresholdHighColor: '#da415f',
      /* PDF 출력 여부 - IE 이슈 대응 */
      isExportingPdf: false,
    };
  },
  computed: {
    baseCapacity() {
      return this.simulationType === simulationTypeDefine.CAPACITY;
    },
    rscColumns() {
      if (this.baseCapacity) {
        const columns = cloneDeep(this.rscBaseColumns);
        columns.push({
          label: '증설',
          scopedSlot: 'capacity-slot',
        });
        return columns;
      }
      return this.rscBaseColumns;
    },
    /**
     * 시뮬레이션 예측 비활성화 여부
     * @author Ju
     * @return {boolean}
     */
    disabledSimulation() {
      if (isEmpty(this.rscSelection)) {
        return true;
      }
      if (this.baseCapacity) {
        // 용량 시뮬레이션일 때 : 선택된 자원 배열 비었는지 & 증설 용량이 비었는지 모두 확인
        const emptyCapacityIdx = findIndex(this.rscSelection, (item) => isNil(item.scaleUpCapacity) || item.scaleUpCapacity === '');
        if (emptyCapacityIdx >= 0) {
          return true;
        }
      }
      return false;
    },
  },
  watch: {
    obj: {
      immediate: true,
      handler(value) {
        if (!isEmpty(value)) {
          this.initialzie();
        } else {
          this.reset();
        }
      },
    },
    predictData: {
      handler(value) {
        if (!isEmpty(value)) {
          this.setSimulationResult();
        } else {
          this.predictPeriodLabel = null;
          this.predictPeriodDetailLabel = null;
          this.predictSummaryDatas = [];
          this.predictDetailDatas = [];
          this.rscCollapseList = [];
        }
      },
    },
  },
  beforeDestory() {
    this.$taskManager.removeTask(this.predictTaskName);
  },
  methods: {
    isEmpty,
    /**
     * 연다 연다 팝업
     * @author Ju
     * @param  {[type]} obj [description]
     * @return {[type]}     [description]
     */
    open(obj) {
      this.obj = obj;
      this.$refs.popup.open();
    },
    /**
     * 닫어 닫어 팝업
     * @author Ju
     * @return {[type]} [description]
     */
    close() {
      this.$refs.smartAnalyzerPopup.close();
      this.$taskManager.removeTask(this.predictTaskName);
    },
    /**
     * 팝업 닫았을 때 ~
     * @author Ju
     * @return {[type]} [description]
     */
    onClose() {
      this.obj = null;
      this.$taskManager.removeTask(this.predictTaskName);
    },
    /**
     * 컴포넌트 초기 설정을 한다.
     * @author Ju
     */
    initialzie() {
      this.objLabel = isEmpty(this.obj.ipaddress) ? getValueAfterCheckEmpty(this.obj.objName) : `${getValueAfterCheckEmpty(this.obj.objName)} (${getValueAfterCheckEmpty(this.obj.ipaddress)})`;
      this.setMaxPredictPeriod();
      this.loadSimulationRscList();
      // 데이터 보관 주기 조회
      predictPerfUtil
        .getDataStoragePeriodBasicinfo()
        .then((result) => {
          if (result.success) {
            this.dataStoragePeriodBasicinfo = result.data;
          } else {
            this.dataStoragePeriodBasicinfo = result.data;
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '데이터 보관 주기 정보', false));
          }
        })
        .catch((result) => {
          this.dataStoragePeriodBasicinfo = result.data;
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '데이터 보관 주기 정보', false));
        });
    },
    /**
     * 장비의 시뮬레이션 대상 자원을 로드한다.
     * @author Ju
     */
    loadSimulationRscList() {
      const params = {
        objId: this.obj.objId,
        managetypeId: this.obj.objManagetypeId,
        objtypeId: this.obj.objtypeId,
      };
      this.rscLoading = true;
      SmartAnalysisApi.getSimulationRscList(params)
        .then((response) => {
          if (response.data.success) {
            const tableData = response.data.data;
            const rscTableDataForCapacity = [];
            forEach(tableData, (item) => {
              if (item.simulationRscType !== 'CPU') {
                item.scaleUpCapacity = 1;
                item.capacityUnit = 'GB';
                rscTableDataForCapacity.push(item);
              }
            });
            this.rscTableDataForCapacity = rscTableDataForCapacity;
            this.rscTableDataForPeriod = tableData;
            this.objRegistDatetime = isEmpty(tableData) ? null : moment(tableData[0].registDatetime).toDate();
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '자원 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '자원 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.rscLoading = false;
        });
    },
    /**
     * 화면 설정을 되돌린다.
     * @author Ju
     */
    reset() {
      this.loading = false;
      this.rscTableData = [];
      this.simulationType = simulationTypeDefine.PERIOD;
      this.predictPeriod = 3;
      this.predictPeriodUnit = predictPerfUtil.predictPeriodUnitDefine.MONTH;
      this.predictData = [];
    },
    /**
     * 최대 예측 기간 값을 설정한다.
     * @author Ju
     */
    setMaxPredictPeriod() {
      this.maxPredictPeriod = predictPerfUtil.getMaxPredictPeriodBaseUnit(this.predictPeriodUnit);
    },
    /**
     * 예측 기간 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    onPredictPeriodChange(value) {
      if (isNil(value) || value === '') {
        // 값이 없는 경우 강제로 최소 값이 설정되도록 한다. 'change' 이벤트가 발신 되는 시점과 currentValue가 설정되는 시점 문제로 nextTick에서 처리
        this.$nextTick(() => {
          this.$refs.predictPeriodField.setCurrentValue(1);
        });
      }
    },
    /**
     * 예측 기간 단위 변경 이벤트 핸들러
     * @author Ju
     * @return {[type]} [description]
     */
    onPredictPeriodUnitChange() {
      this.maxPredictPeriod = predictPerfUtil.getMaxPredictPeriodBaseUnit(this.predictPeriodUnit);
      if (!predictPerfUtil.isValidPredictPeriod(this.predictPeriod, this.predictPeriodUnit)) {
        this.predictPeriod = this.maxPredictPeriod;
      }
    },
    /**
     * 변경한 예측 기간에 맞게 데이터를 설정한다.
     * @author Ju
     */
    setLearnDataBasePredictPeriod(predictPeriod, predictPeriodUnit) {
      const recommendLearnDayCount = predictPerfUtil.getRecommendLearnDayCount(predictPeriod, predictPeriodUnit);
      const minLearnDate = predictPerfUtil.getMinDateByPredictPeriod(this.dataStoragePeriodBasicinfo, removeTime(new Date()), predictPeriod, predictPeriodUnit);
      // 학습 데이터 설정
      const endDatetime = new Date();
      let startDatetime = moment(endDatetime)
        .subtract(recommendLearnDayCount - 1, 'd')
        .toDate();
      if (startDatetime < minLearnDate) {
        startDatetime = minLearnDate;
      }
      return predictPerfUtil.addLearnData([], startDatetime, endDatetime);
    },
    /**
     * 자원 테이블 선택 변경 이벤트 핸들러
     * @author Ju
     * @param  {[type]} selection [description]
     */
    onRscSelectionChange(selection) {
      this.rscSelection = selection;
    },
    /**
     * 시뮬레이션 예측을 수행하낟.
     * @author Ju
     */
    executeSimulation() {
      if (isEmpty(this.rscSelection)) {
        this.$message(MsgUtil.getRequireMsgData('선택', '시뮬레이션할 자원'));
        return;
      }
      const hashkeyIdStrings = [];
      const simulationTargets = [];
      forEach(this.rscSelection, (item) => {
        hashkeyIdStrings.push(item.hashkeyIdString);
        simulationTargets.push(clone(item));
      });

      if (this.baseCapacity) {
        // 용량 기준 시뮬레이션 예측의 경우 3년을 예측하도록 한다.
        this.simulationPredictPeriod = 3;
        this.simulationPredictPeriodUnit = predictPerfUtil.predictPeriodUnitDefine.YEAR;
      } else {
        this.simulationPredictPeriod = this.predictPeriod;
        this.simulationPredictPeriodUnit = this.predictPeriodUnit;
      }
      const learnDataSet = this.setLearnDataBasePredictPeriod(this.simulationPredictPeriod, this.simulationPredictPeriodUnit);
      const learnDateArray = predictPerfUtil.convertLearnDataSetToDateStringArray(learnDataSet, this.objRegistDatetime);
      const predictParams = {
        trainDataList: learnDateArray,
        predictDateTimePeriod: predictPerfUtil.convertToDays(this.simulationPredictPeriod, this.simulationPredictPeriodUnit),
      };

      const me = this;
      this.predictData = null;
      this.loadingText = '시뮬레이션 예측 중입니다. 다소 시간이 소요될 수 있습니다.';

      console.info('-- parmas: ', predictPerfUtil, {
        hashkeyIdStrings,
        predictParamsJson: predictParams,
        learnDataSet,
        simulationPredictPeriod: this.simulationPredictPeriod,
        simulationPredictPeriodUnit: this.simulationPredictPeriodUnit,
      });
      this.loading = true;
      SmartAnalysisApi.executeSimulation({
        hashkeyIdStrings,
        predictParamsJson: JSON.stringify(predictParams),
      })
        .then((response) => {
          if (response.data.success) {
            const predictTargetList = response.data.data;
            this.$taskManager.createTask(
              this.predictTaskName,
              1,
              () => {
                SmartAnalysisApi.checkCompleteSimulation({
                  predictTargetList,
                }).then((result) => {
                  if (result.data.data) {
                    me.$taskManager.removeTask(this.predictTaskName);
                    this.loadSimulationResult(predictTargetList);
                  }
                });
              },
              false,
              300,
              () => {
                me.$taskManager.removeTask(this.predictTaskName);
                this.loading = false;
                this.$message(MsgUtil.getResultMsgData('수행', '시뮬레이션 예측', false));
              },
            );
            this.$taskManager.startTask(this.predictTaskName);
          } else {
            this.$message(MsgUtil.getResultMsgData('수행', '시뮬레이션 예측', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData('수행', '시뮬레이션 예측', false));
          this.loading = false;
          throw new Error(e);
        });
    },
    /**
     * 시뮬레이션 예측 결과를 조회한다.
     * @author Ju
     * @param  {[type]} predictTargetList [description]
     */
    loadSimulationResult(predictTargetList) {
      if (this.baseCapacity) {
        const units = ['', 'KB', 'MB', 'GB', 'TB'];
        forEach(predictTargetList, (item) => {
          const index = findIndex(this.rscSelection, (rsc) => rsc.hashkeyIdString === item.hashkeyIdString);
          const rsc = this.rscSelection[index];
          const square = findIndex(units, (unit) => unit === rsc.capacityUnit) - findIndex(units, (unit) => unit === rsc.rscUnit);
          item.scaleUpValue = rsc.scaleUpCapacity * (1024 ** square);
        });
      }

      SmartAnalysisApi.getSimulationResult({
        predictTargetList,
        baseCapacity: this.baseCapacity,
      })
        .then((response) => {
          if (!response.data.success || isEmpty(response.data.data)) {
            this.$message(MsgUtil.getResultMsgData('수행', '시뮬레이션 예측', false));
          } else {
            this.predictData = response.data.data;
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData('수행', '시뮬레이션 예측', false));
          throw new Error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * 시뮬레이션 예측 결과를 화면에 설정한다.
     * @author Ju
     */
    setSimulationResult() {
      this.simulationColumns = this.baseCapacity ? this.simulationCapacityColumns : this.simulationPeriodColumns;
      const predictPeriodUnitIndex = findIndex(this.predictPeriodUnits, (item) => item.value === this.simulationPredictPeriodUnit);
      this.predictPeriodLabel = `${this.simulationPredictPeriod}${predictPeriodUnitIndex >= 0 ? this.predictPeriodUnits[predictPeriodUnitIndex].label : '년'}`;

      const predictSummaryDatas = [];
      const predictDetailDatas = [];
      forEach(this.predictData, (item) => {
        const rscIndex = findIndex(this.rscTableDataForPeriod, (rsc) => rsc.hashkeyIdString === item.hashkeyIdString);
        const rscInfo = this.rscTableDataForPeriod[rscIndex];
        const predictResult = {
          hashkeyIdString: rscInfo.hashkeyIdString,
          title: `${rscInfo.rscName} (${rscInfo.rscKeyName})`,
          isCpu: rscInfo.simulationRscType === 'CPU',
          // current
          userate: null,
          userateLabel: null,
          totalSizeLabel: null,
          usedSizeLabel: null,
          availSizeLabel: null,
          // predict
          maxValueLabel: null,
          thresholdRowLabel: null,
          thresholdMediumLabel: null,
          thresholdHighLabel: null,
          predictChartOptions: null,
          // recommend
          isRecommendScaleUp: null,
          beforeCapcityLabel: null,
          afterCapcityLabel: null,
          recommendScaleUpLabel: null,
          simulationChartOptions: null,
          orderbyIndex: rscIndex,
        };

        // 현재 자원 값
        predictResult.userate = item.userate;
        predictResult.userateLabel = convertValuByUnit(item.userate, '%', true, true);
        const currentPieChartOptions = cloneDeep(pieChartOptions);
        currentPieChartOptions.title.text = predictResult.userateLabel;
        currentPieChartOptions.series = [
          {
            name: rscInfo.rscName,
            innerSize: '83%',
            data: [
              {
                name: '사용',
                y: item.userate,
              },
              {
                name: '가용',
                y: 100 - item.userate,
              },
            ],
          },
        ];
        let userateColor;
        if (item.userate >= 80) {
          userateColor = this.thresholdRowColor;
        } else if (item.userate >= 90) {
          userateColor = this.thresholdMediumColor;
        } else if (item.userate >= 100) {
          userateColor = this.thresholdHighColor;
        } else {
          userateColor = chartColorSet[0];
        }

        currentPieChartOptions.colors = [userateColor, '#f3f3f4'];
        predictResult.currentPieChartOptions = currentPieChartOptions;
        if (rscInfo.simulationRscType !== 'CPU') {
          predictResult.totalSizeLabel = this.convertBinaryValuByUnit(item.totalSize, rscInfo.rscUnit);
          predictResult.usedSizeLabel = this.convertBinaryValuByUnit(item.usedSize, rscInfo.rscUnit);
          predictResult.availSizeLabel = this.convertBinaryValuByUnit(item.availSize, rscInfo.rscUnit);
        }

        // 시뮬레이션 예측 값 설정
        const predictChartDatas = item.predictChartDatas;
        if (!isEmpty(predictChartDatas)) {
          const format = 'YY.MM.DD hh:mm';
          const firstSeriesData = predictChartDatas[0].data;
          const startDatetime = firstSeriesData[0][0];
          const startDateLabel = moment(startDatetime).format(format);
          const endDateLabel = moment(firstSeriesData[firstSeriesData.length - 1][0]).format(format);
          this.predictPeriodDetailLabel = `${startDateLabel} ~ ${endDateLabel}`;

          const predictChartOptions = cloneDeep(lineChartOptions);
          predictChartOptions.colors = [chartColorSet[0], this.thresholdRowColor, this.thresholdMediumColor, this.thresholdHighColor];
          if (predictChartDatas.length === 4) {
            for (let i = 0; i <= predictChartDatas.length - 1; i += 1) {
              predictChartDatas[i].rscInfo = rscInfo;
              if (i > 0) {
                predictChartDatas[i].lineWidth = 1;
                predictChartDatas[i].dashStyle = 'dash';
                predictChartDatas[i].opacity = 0.5;
                predictChartDatas[i].marker = { enabled: false };
              }
            }
          }
          predictChartOptions.series = predictChartDatas;
          predictChartOptions.chart.height = 160;
          predictResult.predictChartOptions = predictChartOptions;

          if (isEmpty(item.maxInfo)) {
            predictResult.maxValueLabel = EMPTY_TEXT;
          } else {
            predictResult.maxValueLabel = predictResult.isCpu ? convertValuByUnit(item.maxInfo.value, rscInfo.rscUnit) : this.convertBinaryValuByUnit(item.maxInfo.value, rscInfo.rscUnit);
          }
          predictResult.thresholdRowLabel = this.getThresholdValueLabel(item.thresholdRowInfo, startDatetime);
          predictResult.thresholdMediumLabel = this.getThresholdValueLabel(item.thresholdMediumInfo, startDatetime);
          predictResult.thresholdHighLabel = this.getThresholdValueLabel(item.thresholdHighInfo, startDatetime);
        }

        // 용량 즐설 권고 관련 값 설정
        if (!isNil(item.recommendScaleUpValue)) {
          predictResult.isRecommendScaleUp = true;
          let beforeCapcityLabel;
          let afterCapcityLabel;
          let recommendScaleUpValueLabel;
          if (predictResult.isCpu) {
            beforeCapcityLabel = `${item.cpuCoreCount}코어`;
            afterCapcityLabel = `${item.cpuCoreCount + item.recommendScaleUpValue}코어`;
            recommendScaleUpValueLabel = `${item.recommendScaleUpValue}코어`;
          } else {
            beforeCapcityLabel = `${this.convertBinaryValuByUnit(item.totalSize, rscInfo.rscUnit)}`;
            afterCapcityLabel = `${this.convertBinaryValuByUnit(item.totalSize + item.recommendScaleUpValue, rscInfo.rscUnit)}`;
            recommendScaleUpValueLabel = `${this.convertBinaryValuByUnit(item.recommendScaleUpValue, rscInfo.rscUnit)}`;
          }
          predictResult.beforeCapcityLabel = beforeCapcityLabel;
          predictResult.afterCapcityLabel = afterCapcityLabel;
          predictResult.recommendScaleUpLabel = `${recommendScaleUpValueLabel} 증설 (${beforeCapcityLabel} → ${afterCapcityLabel})`;

          if (!isEmpty(item.simulationChartDatas)) {
            const simulationChartOptions = cloneDeep(lineChartOptions);
            const firstSeriesData = item.simulationChartDatas[0].data;
            const centerIndex = Math.round(firstSeriesData.length / 2);
            forEach(item.simulationChartDatas, (series) => {
              series.rscInfo = { rscUnit: '%' };
            });
            simulationChartOptions.series = item.simulationChartDatas;
            simulationChartOptions.tooltip = {
              split: false,
              shared: true,
              formatter() {
                let tooltip = '';
                this.points.forEach((point) => {
                  const rscUnit = isEmpty(point.series.userOptions.rscInfo) ? null : point.series.userOptions.rscInfo.rscUnit;
                  const yValue = rscUnit === 'count' ? convertValueByUnitData(point.y, 'count', '개', 1, null, null, true) : convertValuByUnit(point.y, rscUnit, true);
                  tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
                });
                return tooltip;
              },
            };
            simulationChartOptions.xAxis = {
              tickWidth: 0,
              labels: false,
              plotBands: [
                {
                  from: firstSeriesData[centerIndex][0],
                  to: firstSeriesData[firstSeriesData.length - 1][0],
                  color: 'rgba(68, 170, 213, .2)',
                },
              ],
            };
            predictResult.simulationChartOptions = simulationChartOptions;
          }
        } else {
          predictResult.isRecommendScaleUp = false;
          predictResult.recommendScaleUpLabel = '없음';
        }
        predictDetailDatas.push(predictResult);
        const preidctTableData = {
          rscName: predictResult.title,
          currentPerf: predictResult.isCpu ? predictResult.userateLabel : `${predictResult.userateLabel} (${predictResult.totalSizeLabel} 중 ${predictResult.usedSizeLabel} 사용)`,
          threshold: predictResult.thresholdRowLabel,
          isRecommend: predictResult.isRecommendScaleUp,
          recommendContent: predictResult.recommendScaleUpLabel,
          orderbyIndex: rscIndex,
        };

        if (this.baseCapacity) {
          preidctTableData.condition = `${rscInfo.scaleUpCapacity}${rscInfo.capacityUnit} (${this.convertBinaryValuByUnit(item.totalSize, rscInfo.rscUnit)} → ${this.convertBinaryValuByUnit(
            item.totalSize + item.scaleUpValueForSimulation,
            rscInfo.rscUnit,
          )})`;
          preidctTableData.result = isNil(item.availMaxDatetime) ? '3년 이상 운용 가능' : `${moment(item.availMaxDatetime).format('YYYY년 MM월 DD')}까지 운용 가능`;
        }
        predictSummaryDatas.push(preidctTableData);
      });
      this.predictDetailDatas = sortBy(predictDetailDatas, (item) => item.orderbyIndex);
      this.predictSummaryDatas = sortBy(predictSummaryDatas, (item) => item.orderbyIndex);
      this.rscCollapseList = map(predictDetailDatas, 'hashkeyIdString');
    },
    /**
     * 바이너리 값을 환산한다.
     * @author Ju
     * @param  {[type]} value [description]
     * @param  {[type]} unit  [description]
     * @return {String}
     */
    convertBinaryValuByUnit(value, unit) {
      const unitData = getUnitData(unit);
      return convertValueByUnitData(value, unit, 'B', unitData.fractionDigits, unitData.divisor, unitData.prefixUnit, true, true);
    },
    /**
     * 임계치 값에 대한 라벨 텍스트를 반환한다.
     * @author Ju
     * @param  {[type]} thresholdInfo [description]
     * @param  {[type]} startDatetime [description]
     * @return {String}               [description]
     */
    getThresholdValueLabel(thresholdInfo, startDatetime) {
      let label;
      if (isEmpty(thresholdInfo)) {
        label = '초과 없음';
      } else {
        const exceedDatetime = moment(thresholdInfo.datetime);
        const diff = moment(exceedDatetime).diff(startDatetime, 'd');
        label = `${exceedDatetime.format('YY년 MM월 DD일')} (${diff}일 후) 초과`;
      }
      return label;
    },
    /**
     * 자원 용량 증설량 변경 이벤트 핸들러
     * @author Ju
     */
    onScaleUpCapacityChange() {
      const emptyCapacityIdx = findIndex(this.rscTableDataForCapacity, (item) => isNil(item.scaleUpCapacity) || item.scaleUpCapacity === '');
      if (emptyCapacityIdx >= 0) {
        this.$nextTick(() => {
          forEach(this.rscTableDataForCapacity, (item) => {
            if (isNil(item.scaleUpCapacity) || item.scaleUpCapacity === '') {
              item.scaleUpCapacity = 1;
            }
          });
        });
      }
    },
    /**
     * 지정된 HTML 영역을 PDF로 출력한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    exportPdf() {
      const tempRscCollapseList = clone(this.rscCollapseList);
      this.rscCollapseList = map(this.predictDetailDatas, 'hashkeyIdString'); // 모든 자원의 상세 컨텐츠를 연다.

      const agent = navigator.userAgent.toLowerCase();
      const isIE = (navigator.appName === 'Netscape' && agent.indexOf('trident') !== -1) || (agent.indexOf('mise') !== -1);

      try {
        this.loadingText = 'PDF로 저장 중..';
        this.loading = true;

        /* IE 이슈 대응 - 하이차트 html2canvas로 캡처되지 않는 문제 */
        if (isIE) {
          // 브라우저가 IE일 경우 하이차트 영역을 이미지로 변환 후 출력한다.
          this.isExportingPdf = true;
          // 하이차트 목록을 조회해온 뒤 > export-pdf 클래스의 차트만 가공한다.
          forEach(Highcharts.charts, (chart) => {
            if (!isEmpty(chart) && chart.renderTo.className.indexOf('export-pdf') !== -1) {
              const className = chart.renderTo.className;
              const chartWidth = chart.chartWidth;
              const chartHeight = chart.chartHeight;

              // 하이차트 svg 영역을 이미지로 변경하기 위한 canvas 엘리먼트의 임시 생성
              const canvas = document.createElement('canvas');
              canvas.setAttribute('width', chartWidth);
              canvas.setAttribute('height', chartHeight);
              if (className.indexOf('circular') === -1) {
                // 파이차트가 아닐 때는 타이틀 제거
                canvg(canvas, chart.getSVG({ chart: { width: chartWidth, height: chartHeight }, title: false }));
              } else {
                // 파이차트일 때는 타이틀 유지
                canvg(canvas, chart.getSVG({ chart: { width: chartWidth, height: chartHeight } }));
              }

              const imgUrl = canvas.toDataURL('image/png');
              const imgEl = this.$el.querySelector(`img.${className}`);
              imgEl.setAttribute('width', chartWidth);
              imgEl.setAttribute('height', chartHeight);
              imgEl.setAttribute('src', imgUrl);
            }
          });

          /* 이미지 렌더링 속도 맞추기 위해 500ms 이후 출력 함수 실행 */
          setTimeout(() => {
            this.getHtmlToPdf(tempRscCollapseList);
          }, 500);
        } else {
          // 브라우저가 IE가 아닐 때에는 가공 없이 출력한다.
          this.getHtmlToPdf(tempRscCollapseList);
        }
      } catch (e) {
        this.isExportingPdf = false;
        this.$message(MsgUtil.getResultMsgData('생성', 'PDF', false));
        this.loading = false;
        this.rscCollapseList = tempRscCollapseList;
        throw new Error(e);
      }
    },
    /**
     * Html2Canvas로 PDF 변환할 HTML 영역을 캡처해 canvas 객체로 저장하고 PDF 파일로 출력한다.
     * @author Minyeong
     * @param  {[type]} tempRscCollapseList [description]
     * @return {[type]}                     [description]
     */
    getHtmlToPdf(tempRscCollapseList) {
      // source : PDF로 변환할 HTML 영역
      const source = this.$el.querySelector('.save-for-pdf');

      // 성능 개선 : 출력 외 영역(팝업 외 영역)은 클로닝/렌더링하지 않도록 attribute 추가
      const app = document.querySelector('#app');
      app.setAttribute('data-html2canvas-ignore', true);

      // 단위 변환(px > pt) 시 필요한 constants
      const webDPI = 96; // Window 기준 WEB DPI
      const pdfDPI = 72; // PDF 캔버스 기준 DPI
      const A4Width = 210; // A4 가로길이 (mm)
      const A4Height = 297; // A4 세로길이 (mm)
      const convertInch = 2.54; // 인치 변환 계수

      // A4용지 사이즈를 WEB DPI 기준 픽셀 사이즈로 변환
      const A4PxHeight = (A4Height / (10 * convertInch)) * webDPI;

      // 화질 개선 Scale Factor (커질수록 화질 향상, 기본값은 1), 출력 크기 조절용 Parameter (출력 영역 가로길이 > PDF 가로길이)
      const scaleFactor = 2;
      const scaleParam = 1.2;

      // PDF 상하/좌우 여백
      const margin = 50 * scaleFactor;

      /**
       * jdpdf 객체 생성
       * @param [용지방향] 'p'(default) : 세로, 'l' : 가로
       * @param [단위] 'pt'(default), 'mm' ...
       * @param [PDF 사이즈] 'a4'(default), 'letter', 'a6' ... [width, height]로 용지 크기를 정해줄 수 있음
       */
      const pdf = new JsPdf('p', 'pt', 'a4');

      // PDF의 가로 길이(좌우여백 제외, a4기준)
      const pageWidth = 190;
      const pageHeight = pageWidth * 1.414;
      const imgWidth = pageWidth;
      Html2Canvas(source, { scale: scaleFactor, allowTaint: true, logging: true }).then((canvas) => {
        // 캡쳐된 이미지의 세로 길이 단위 변환 (px > mm) 후 출력해야 할 PDF의 페이지 수 계산
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pageNum = imgHeight / pageHeight;

        // page개수 만큼 반복해 이미지를 PDF 객체에 set
        for (let page = 0; page < pageNum; page += 1) {
          const sourceImage = canvas;

          // canvas 엘리먼트에 캡쳐된 이미지를 한 페이지 사이즈로 잘라 저장하기 위한 parameter
          // 1. Source Image
          const sourceX = 0;
          const sourceY = pageNum > 1 ? ((A4PxHeight * scaleFactor * scaleParam) - margin) * page : 0;
          const sourceWidth = sourceImage.width;
          const sourceHeight = pageNum > 1 ? (A4PxHeight * scaleFactor * scaleParam) - margin : sourceImage.height;

          // 2. Canvas
          const destX = margin; // LEFT margin
          const destY = margin; // TOP margin
          const destWidth = sourceImage.width - margin;
          const destHeight = sourceHeight - margin;

          // canvas 엘리먼트의 width와 height 지정 (WEB 픽셀 사이즈 기준 > Windows일 때)
          const onePageCanvas = document.createElement('canvas');

          onePageCanvas.setAttribute('width', sourceImage.width);
          onePageCanvas.setAttribute('height', pageNum > 1 ? sourceImage.height - margin : sourceImage.height);

          const ctx = onePageCanvas.getContext('2d');
          // canvas 엘리먼트에 캡쳐된 이미지를 현재 페이지에 맞게 잘라 set
          ctx.drawImage(sourceImage, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);

          // png > jpeg로 형식 변경하면서 캔버스의 투명 영역이 검정색으로 바뀌는 문제를 수정하기 위한 이미지 비트 연산작업
          const tempImg = ctx.getImageData(0, 0, onePageCanvas.width, onePageCanvas.height);
          const data = tempImg.data;
          for (let bit = 0; bit < data.length; bit += 4) {
            if (data[bit + 3] < 255) {
              data[bit] = 255 - data[bit];
              data[bit + 1] = 255 - data[bit + 1];
              data[bit + 2] = 255 - data[bit + 2];
              data[bit + 3] = 255 - data[bit + 3];
            }
          }
          ctx.putImageData(tempImg, 0, 0);

          const imgData = onePageCanvas.toDataURL('image/jpeg', 1.0);
          const width = onePageCanvas.width;
          const height = onePageCanvas.height;

          if (page > 0) {
            // a4사이즈로 페이지를 추가함 (PDF 캔버스 DPI 기준)
            pdf.addPage((A4Width / (10 * convertInch)) * pdfDPI, (A4Height / (10 * convertInch)) * pdfDPI);
          }
          pdf.setPage(page + 1);
          pdf.addImage(imgData, 'JPEG', 0, 0, (width * 0.6) / scaleFactor, (height * 0.6) / scaleFactor);
        }
        pdf.save(`시뮬레이션예측결과_${this.obj.objName}_${this.obj.ipaddress}_${moment(new Date()).format('YYYYMMDD')}.pdf`);
        this.$message(MsgUtil.getResultMsgData('생성', 'PDF', true));
      }).catch((e) => {
        this.$message(MsgUtil.getResultMsgData('생성', 'PDF', false));
        throw new Error(e);
      }).finally(() => {
        // 임시로 추가했던 data-htrml2canvas-ignore attribute 제거
        app.removeAttribute('data-html2canvas-ignore');
        this.isExportingPdf = false;
        this.loading = false;
        this.rscCollapseList = tempRscCollapseList;
      });
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/_variable.scss';
@mixin fontawesome {
  font-family: 'Font Awesome 5 Free', 'Font Awesome 5 Pro';
}
@mixin element-icons {
  font-family: 'element-icons';
}
.simulation-result-wrapper {
  width: 100%;
  .warning {
    font-size: $text-12;
    color: #e43757;
    &:before {
      @include element-icons content: '\e7a3';
      font-weight: 900;
      font-size: $text-small;
      position: relative;
      top: -1px;
      margin-right: 3px;
    }
  }
  .info-box {
    padding: 10px 15px;
    border: 1px solid #d7dbe1;
    li {
      font-size: $text-medium;
      line-height: 26px;
      color: $deepgray;
      .stress {
        display: inline-block;
        width: 100px;
        margin-right: 10px;
        color: black;
        font-weight: 500;
      }
      &:before {
        content: '.';
        font-size: 20px;
        color: #b9babb;
        margin-right: 10px;
        position: relative;
        top: -3px;
      }
    }
  }

  .result-box {
    $chart-height: 160px;
    .title {
      color: black;
      margin-bottom: 10px;
      &:before {
        content: '.';
        font-size: 20px;
        font-weight: 900;
        color: $luckygray;
        margin-right: 10px;
        position: relative;
        top: -3px;
      }
    }

    .current-status {
      width: 223px;
      margin-right: 10px;
      .chart-box {
        position: relative;
        justify-content: center;
        align-items: center;
        text-align: center;
        vertical-align: middle;
        height: $chart-height !important;
      }

      .text-box {
        margin-left: 15px;
        color: $deepgray;
        font-size: $text-medium;
        text-align: center;
        .detail {
          font-size: $text-12;
          background: #ebf4fc;
          padding: 5px 10px;
          border-radius: 12px;
        }
      }
    }

    .predict-result {
      width: calc(100% - 236px);
      .max-value-info {
        color: $deepgray;
        font-size: $text-12;
        float: right;
        margin-right: 5px;
        position: relative;
        top: 5px;
      }

      .chart-box {
        height: $chart-height !important;
      }

      .threshold-box {
        margin-left: 15px;
        color: $deepgray;
        font-size: $text-medium;
        width: 100%;
        height: 40px;
        font-size: $text-12;
        background: #f4f5f6;
        padding: 15px;
        margin-top: 14px;
        font-weight: bold;
        line-height: 1.15;
        li {
          display: inline-block;
          margin-right: 10px;
          float: left;
          span {
            display: inline-block;
            margin-right: 6px;
            font-weight: bold;
            font-weight: 500;
          }
        }
      }
    }
  }

  .highcharts-legend-item tspan {
    font-size: 11px;
    font-weight: 400;
  }

  hr {
    margin: 10px 0;
    border-top: 0;
    border-bottom: 1px solid $selectedgray;
  }
}

.condition-set-box .ll-table-container .el-input__inner {
  height: 21px;
}
</style>



// WEBPACK FOOTER //
// src/views/performance/node/popup/components/button/common/SimulationPredictPopupButton/SimulationPredictPopup.vue